import { useEffect } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { breakpoints, Card, Checkbox, Heading, Inline, RadioCard, RadioGroup, Stack, Text, textVariants, ValidationMessage, vars } from '@etg/wings';
import { css, cx } from '@eti/styles';
import { useSiteContext, useTranslation } from '@eti/providers';
import { DangerousHtmlContent, Illustration, Pictogram, ScreenReaderPrice } from '@eti/components';
import checkIcon from '@eti/assets/icons/checkList.svg?url';
import { screenReaderOnly } from '../../common/utils/styles';
import { useDynamicIcon } from '../../common/utils/dynamic-icons/useDynamicIcon';
import { DYNAMIC_ICON } from '../../common/utils/dynamic-icons/icons';
import { useGetData } from '../hooks/useGetData';
import ReadMore from './ReadMore';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const wrapperStyles = iconContent => css`
  a {
    position: relative;

    &[target='_blank']::after {
      content: ${iconContent};
      margin-inline-start: 4px;
      position: relative;
      top: 5px;
    }
  }
`;
const headerStyles = css`
  flex: 1;
`;
const titleAndPictogramStyles = css`
  margin-bottom: 2px;
  margin-top: 16px;
`;
const descriptionStyles = css`
  font-size: 1rem;
`;
const priceTagStyles = css`
  font-size: 1.25rem;
  font-weight: 700;
`;
const radioGroupStyles = css`
  > label {
    font-weight: 700;
  }
`;
const productDescriptionStyles = css`
  line-height: 150%;
  padding-inline-start: 58px;

  & ul {
    margin-top: 8px;
  }

  & li {
    padding-inline-start: 24px;
    position: relative;

    &::before {
      background: url(${checkIcon});
      background-repeat: no-repeat;
      background-size: contain;
      content: '';
      display: inline-block;
      height: 16px;
      inset-inline-start: 0;
      margin-inline-end: 8px;
      position: absolute;
      top: 3px;
      width: 16px;
    }
  }

  li:not(:last-child) {
    margin-bottom: 8px;
  }
`;
const MutuallyExclusive = ({
  className,
  emptyOptionId: emptyOptionCheckBoxId,
  input,
  isAnyProductSelectedInBundle,
  meta,
  numberOfTravelers,
  products,
  selection
}) => {
  const {
    t
  } = useTranslation();
  const {
    brand: {
      code: brand
    }
  } = useSiteContext();
  const isTabletOrDesktop = useMediaQuery(`(min-width: ${breakpoints._768})`);
  const {
    touched: isTouched,
    error: errorMessage
  } = meta;
  const {
    onChange
  } = input;
  const onlyPriceLabel = t('Products.Price.Label.Only');
  const totalPriceLabel = t('Products.Price.Label.Total');
  const perTravelerLabel = t('Products.Price.Label.Per.Person');
  const hasError = Boolean(isTouched && errorMessage);
  const productsData = useGetData(products, numberOfTravelers);
  const [icon] = useDynamicIcon(vars.colors.links.default, DYNAMIC_ICON.linkIcon);
  const getPriceToShow = (showPricePerTraveler, productId, totalPrice, pricePerTraveler) => {
    return !showPricePerTraveler || numberOfTravelers === 1 || selection === productId ? totalPrice : pricePerTraveler;
  };
  const getPriceLabel = (showPricePerTraveler, productId) => {
    if (selection === productId) {
      return totalPriceLabel;
    }
    return !showPricePerTraveler || numberOfTravelers === 1 ? onlyPriceLabel : perTravelerLabel;
  };
  useEffect(() => {
    if (isAnyProductSelectedInBundle) {
      onChange(emptyOptionCheckBoxId);
    }
  }, [isAnyProductSelectedInBundle, onChange, emptyOptionCheckBoxId]);
  if (isAnyProductSelectedInBundle) {
    return null;
  }
  return /*#__PURE__*/_jsx(Card, {
    className: cx(wrapperStyles(icon), className),
    "data-testid": "mutually-exclusive-container",
    children: /*#__PURE__*/_jsxs(Stack, {
      spacing: 16,
      children: [/*#__PURE__*/_jsxs(Inline, {
        spacing: 16,
        children: [isTabletOrDesktop && /*#__PURE__*/_jsx(Illustration, {
          id: "insurance-small",
          width: 126
        }), /*#__PURE__*/_jsxs(Stack, {
          className: headerStyles,
          spacing: 8,
          children: [/*#__PURE__*/_jsxs(Inline, {
            className: titleAndPictogramStyles,
            spacing: 8,
            children: [!isTabletOrDesktop && /*#__PURE__*/_jsx(Pictogram, {
              brand: brand,
              id: "bankruptcy-insurance",
              size: "32px"
            }), /*#__PURE__*/_jsx(Heading, {
              "data-testid": "mutually-exclusive-title",
              level: 2,
              children: t('Products.MutuallyExclusive.Title')
            })]
          }), /*#__PURE__*/_jsx("div", {
            className: descriptionStyles,
            "data-testid": "mutually-exclusive-description",
            children: t('Products.MutuallyExclusive.Description')
          })]
        })]
      }), /*#__PURE__*/_jsxs(RadioGroup, {
        className: radioGroupStyles,
        hasError: hasError,
        name: "cardSectionChildrenExample",
        onChange: e => onChange(e.target.value),
        selectedValue: selection,
        children: [/*#__PURE__*/_jsx(RadioGroup.Legend, {
          className: screenReaderOnly,
          children: t('Products.MutuallyExclusive.Description')
        }), /*#__PURE__*/_jsxs(Stack, {
          spacing: 16,
          children: [hasError && errorMessage && /*#__PURE__*/_jsx(ValidationMessage, {
            "data-testid": "mutually-exclusive-error-message",
            children: errorMessage
          }), productsData === null || productsData === void 0 ? void 0 : productsData.map(({
            productTitle,
            productDescription,
            productReadMore,
            productId,
            totalPrice,
            pricePerTraveler,
            showPricePerTraveler
          }) => {
            const priceLabel = getPriceLabel(showPricePerTraveler, productId);
            const price = getPriceToShow(showPricePerTraveler, productId, totalPrice, pricePerTraveler);
            return /*#__PURE__*/_jsxs(RadioCard, {
              "aria-describedby": `price-label-${productId} priceToPay-${productId}`,
              "data-offer-rate": productId,
              "data-testid": `mutually-exclusive-option-${productId}`,
              id: `mutually-exclusive-option-${productId}`,
              label: /*#__PURE__*/_jsx(Text, {
                variant: textVariants._18_EMPHASIZED,
                children: productTitle
              }),
              value: String(productId),
              children: [/*#__PURE__*/_jsx(Card.Section, {
                className: productDescriptionStyles,
                children: /*#__PURE__*/_jsxs(Stack, {
                  spacing: 8,
                  children: [/*#__PURE__*/_jsx(DangerousHtmlContent, {
                    content: productDescription,
                    "data-testid": `mutually-exclusive-abstract-text-${productId}`
                  }), productReadMore && /*#__PURE__*/_jsx(ReadMore, {
                    content: productReadMore,
                    productId: productId
                  })]
                })
              }), /*#__PURE__*/_jsx(Card.Section, {
                children: /*#__PURE__*/_jsxs(Inline, {
                  align: "end",
                  alignY: "center",
                  children: [/*#__PURE__*/_jsx(Text, {
                    id: `price-label-${productId}`,
                    children: priceLabel
                  }), /*#__PURE__*/_jsx(ScreenReaderPrice, {
                    "data-testid": `mutuallyExclusive-${productId}-screenReader-price`,
                    id: `priceToPay-${productId}`,
                    price: price
                  }), /*#__PURE__*/_jsx(Text, {
                    "aria-hidden": "true",
                    className: priceTagStyles,
                    "data-testid": `mutuallyExclusive-${productId}-price`,
                    children: price
                  })]
                })
              })]
            }, productId);
          })]
        })]
      }), /*#__PURE__*/_jsxs(Inline, {
        align: "end",
        "data-testid": "mutually-exclusive-empty-option",
        spacing: 16,
        children: [/*#__PURE__*/_jsx(Checkbox.Label, {
          htmlFor: emptyOptionCheckBoxId,
          children: t('Product.Bundle.Selection.Empty.Option.Label')
        }), /*#__PURE__*/_jsx(Checkbox, {
          checked: selection === emptyOptionCheckBoxId,
          hasError: hasError,
          id: emptyOptionCheckBoxId,
          onChange: () => onChange(emptyOptionCheckBoxId),
          value: emptyOptionCheckBoxId
        })]
      })]
    })
  });
};
export default MutuallyExclusive;
//# sourceMappingURL=MutuallyExclusive.js.map